import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import {
  H2,
  H6,
  Line,
  Button,
  Input,
  Flex,
  CheckboxInput,
  TextboxInput,
  SelectDropdown,
  InputFile,
  DatePicker,
} from '../../lib/components'
import { IFormProps, NewOpportunity, FileDataProps, list } from '../../lib/types'
import { PublishingStatus } from '../../lib/types/opportunityTypes'
import { FormikProps, Formik, FieldArray, getIn } from 'formik'
import { UploadedImage } from './uploaded-image'
import { COUNTRIES, ASSET_CLASS, ERROR } from '../../lib/utils/message'
import { createSlug, getFileKeyFromSignedUrl } from '../../lib/utils/helpers'
import { opportunityServices } from '../../services'
import './opportunity-form.scss'
import { Dropdown } from '../../lib/components/Input/input-component'
import DynamicFieldMappingEditor from './termsheet-field-mapping'
import {
  OPPORTUNITY_CURRENCY_OPTIONS,
  OPPORTUNITY_VISIBILITY_OPTIONS,
} from './opportunity-form-constants'
import { buildTouchedFromErrors, scrollToFirstFormError } from './opportunity-form-validation'
import { StatusLabel } from '../../lib/components/StatusLabel'
import { showBanner } from '../../global-state/banner-slice'
import {
  buildMarketingSignupPathLink,
  buildMarketingSignupQueryLink,
  CAMPAIGN_UTM_PARAM_KEYS,
  CAMPAIGN_UTM_PARAM_LABELS,
  CampaignUtmParams,
  emptyCampaignUtmParams,
} from './marketing-signup-link'

const OpportunityFormPresenter = ({
  initialValues,
  handleFormSubmit,
  validationSchema,
  handleFileUpload,
  handleErrorMessage,
  sectorOptions = [],
}: IFormProps<NewOpportunity, NewOpportunity> & { sectorOptions?: list[] }) => {
  const dispatch = useDispatch()
  const params = useParams()
  const { id, projectSlug } = params
  const [signedImageUrl, setSignedImageUrl] = useState({
    cover_image: '',
    logo_image: '',
    logo_wide_image: '',
    marketing_signup_image: '',
    marketing_signup_mobile_image: '',
  })
  const [uploadFilesMetaData, setUploadFilesMetaData] = useState<{
    documents: FileDataProps[]
    cover_image: FileDataProps
    logo_image: FileDataProps
    logo_wide_image: FileDataProps
    marketing_signup_image: FileDataProps
    marketing_signup_mobile_image: FileDataProps
  }>({
    documents: [],
    cover_image: {} as FileDataProps,
    logo_image: {} as FileDataProps,
    logo_wide_image: {} as FileDataProps,
    marketing_signup_image: {} as FileDataProps,
    marketing_signup_mobile_image: {} as FileDataProps,
  })
  const [exchange, setExchange] = useState<string>('')
  const [campaignUtmParams, setCampaignUtmParams] =
    useState<CampaignUtmParams>(emptyCampaignUtmParams)

  const copyLinkToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      dispatch(showBanner({ text: 'Link copied to clipboard', variant: 'success-banner' }))
    } catch {
      dispatch(
        showBanner({
          text: 'Failed to copy link. Please copy manually.',
          variant: 'error-banner',
        }),
      )
    }
  }

  useEffect(() => {
    const populateSignedCoverImage = async () => {
      const coverImagePreview = await generatePreview(initialValues.cover_image)
      const logoImagePreview = await generatePreview(initialValues.logo_image ?? '')
      const logoWideImagePreview = await generatePreview(initialValues.logo_wide_image ?? '')
      const marketingSignupImagePreview = await generatePreview(
        initialValues.marketing_signup_image ?? '',
      )
      const marketingSignupMobileImagePreview = await generatePreview(
        initialValues.marketing_signup_mobile_image ?? '',
      )
      setSignedImageUrl({
        cover_image: coverImagePreview.url,
        logo_image: logoImagePreview.url,
        logo_wide_image: logoWideImagePreview.url,
        marketing_signup_image: marketingSignupImagePreview.url,
        marketing_signup_mobile_image: marketingSignupMobileImagePreview.url,
      })
      setUploadFilesMetaData((oldData) => ({
        ...oldData,
        cover_image: coverImagePreview
          ? {
              name: coverImagePreview.metadata.filename,
              type: coverImagePreview.metadata.type,
              size: coverImagePreview.metadata.size,
              date: coverImagePreview.metadata.lastModified,
            }
          : ({} as FileDataProps),
        logo_image: logoImagePreview
          ? {
              name: logoImagePreview.metadata.filename,
              type: logoImagePreview.metadata.type,
              size: logoImagePreview.metadata.size,
              date: logoImagePreview.metadata.lastModified,
            }
          : ({} as FileDataProps),
        logo_wide_image: logoWideImagePreview
          ? {
              name: logoWideImagePreview.metadata.filename,
              type: logoWideImagePreview.metadata.type,
              size: logoWideImagePreview.metadata.size,
              date: logoWideImagePreview.metadata.lastModified,
            }
          : ({} as FileDataProps),
        marketing_signup_image: marketingSignupImagePreview
          ? {
              name: marketingSignupImagePreview.metadata.filename,
              type: marketingSignupImagePreview.metadata.type,
              size: marketingSignupImagePreview.metadata.size,
              date: marketingSignupImagePreview.metadata.lastModified,
            }
          : ({} as FileDataProps),
        marketing_signup_mobile_image: marketingSignupMobileImagePreview
          ? {
              name: marketingSignupMobileImagePreview.metadata.filename,
              type: marketingSignupMobileImagePreview.metadata.type,
              size: marketingSignupMobileImagePreview.metadata.size,
              date: marketingSignupMobileImagePreview.metadata.lastModified,
            }
          : ({} as FileDataProps),
      }))
    }

    const populateUploadMetadata = async () => {
      const documentsPreview =
        initialValues?.documents && initialValues?.documents?.[0]?.title !== ''
          ? await Promise.all(initialValues.documents.map(async (doc) => generatePreview(doc.url)))
          : null

      setUploadFilesMetaData((oldData) => ({
        ...oldData,
        documents: documentsPreview
          ? documentsPreview.map((doc) => ({
              name: doc.metadata.filename,
              type: doc.metadata.type,
              size: doc.metadata.size,
              date: doc.metadata.lastModified,
            }))
          : [],
      }))
    }

    if (initialValues.opportunity_title !== '') {
      populateSignedCoverImage()
      populateUploadMetadata()
    }
  }, [initialValues])

  const generatePreview = async (key: any) => {
    const newURL = (key as any).replaceAll('/', '%2F')

    const signedUrl = getFileKeyFromSignedUrl(newURL)
    const data = await opportunityServices.previewFile(signedUrl)
    if (data && data.data && data.status === 200) return data.data
    return ''
  }

  return (
    <>
      {' '}
      <div className='project-form-container'>
        <H2
          classes={['mb-48']}
          text={id && projectSlug ? 'Edit Opportunity' : 'Add New Opportunity'}
        />
        <Formik
          enableReinitialize
          initialValues={initialValues}
          handleFileUpload={handleFileUpload}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {(formik) => {
            const {
              values,
              touched,
              errors,
              dirty,
              setFieldValue,
              setFieldTouched,
              handleSubmit,
              validateForm,
              setTouched,
            }: FormikProps<typeof initialValues> = formik

            const currentAssetClass = values.asset_class?.trim()
            const assetClassOptions =
              currentAssetClass && !ASSET_CLASS.some((option) => option.value === currentAssetClass)
                ? [
                    { text: currentAssetClass, value: currentAssetClass, disabled: true },
                    ...ASSET_CLASS,
                  ]
                : ASSET_CLASS

            const handleSaveClick = async () => {
              const validationErrors = await validateForm()
              if (validationErrors && Object.keys(validationErrors).length > 0) {
                setTouched(buildTouchedFromErrors(validationErrors) as typeof touched, true)
                window.requestAnimationFrame(() => {
                  scrollToFirstFormError(validationErrors)
                })
                return
              }
              handleSubmit()
            }

            return (
              <form>
                <section className='form-section'>
                  <H6 classes={['mb-32', 'form-section-title']} text='Basic information' />
                  <Line />
                  <div className='form-grid form-grid--cols-3'>
                    <div className='field-container'>
                      <Input
                        name='opportunity_title'
                        labelText='Opportunity Name *'
                        dataTestId='opportunity-name'
                        placeholder='Opportunity Name'
                        type='text'
                        required
                        value={values.opportunity_title}
                        handleChange={(e) => {
                          setFieldValue('opportunity_title', e.target.value)
                          setFieldValue('opportunity_slug', createSlug(e.target.value))
                        }}
                        dirty={dirty}
                        errors={{
                          opportunity_title:
                            !!errors && errors.opportunity_title ? errors.opportunity_title : null,
                        }}
                        touched={{
                          opportunity_title: !!touched && !!touched.opportunity_title,
                        }}
                      />
                    </div>
                    <div className='field-container'>
                      <Input
                        name='opportunity_slug'
                        labelText='Opportunity Slug *'
                        dataTestId='opportunity-slug'
                        placeholder='Unique Opportunity Slug'
                        type='text'
                        required
                        value={createSlug(values.opportunity_title)}
                        disabled
                      />
                    </div>
                    <div className='field-container'>
                      <Input
                        name='opportunity_domain'
                        labelText='Company domain'
                        dataTestId='opportunity-domain'
                        placeholder='example.com'
                        type='text'
                        value={values.opportunity_domain ?? ''}
                        handleChange={(e) => setFieldValue('opportunity_domain', e.target.value)}
                        dirty={dirty}
                        errors={{
                          opportunity_domain:
                            !!errors && errors.opportunity_domain
                              ? errors.opportunity_domain
                              : null,
                        }}
                        touched={{
                          opportunity_domain: !!touched && !!touched.opportunity_domain,
                        }}
                      />
                    </div>
                    <div className='field-container'>
                      <div className='input-label'> Asset Class *</div>
                      <SelectDropdown
                        name='asset_class'
                        id='assetClassId'
                        formId=''
                        list={assetClassOptions}
                        dataTestId='opportunity-asset-class'
                        value={values.asset_class?.trim() || 'Private Equity'}
                        handleChange={(val: any) => setFieldValue('asset_class', val)}
                        dirty={dirty}
                        errors={{
                          asset_class: !!errors && errors.asset_class ? errors.asset_class : null,
                        }}
                        touched={{
                          asset_class: !!touched && !!touched.asset_class,
                        }}
                      />
                    </div>
                  </div>
                  <div className='form-grid form-grid--cols-3'>
                    <div className='field-container'>
                      <div className='input-label'> Sector *</div>
                      <SelectDropdown
                        name='sector_id'
                        id='sectorDropdownId'
                        formId=''
                        placeholder='Select sector'
                        list={sectorOptions}
                        dataTestId='opportunity-sector'
                        value={values.sector_id ? String(values.sector_id) : ''}
                        handleChange={(val: string) =>
                          setFieldValue('sector_id', val ? Number(val) : null)
                        }
                        dirty={dirty}
                        errors={{
                          sector_id: !!errors && errors.sector_id ? errors.sector_id : null,
                        }}
                        touched={{ sector_id: !!touched && !!touched.sector_id }}
                      />
                    </div>
                    <div className='field-container'>
                      <div className='input-label'> Jurisdiction *</div>
                      <SelectDropdown
                        name='jurisdiction'
                        id='countryDropdownId'
                        formId=''
                        list={COUNTRIES}
                        dataTestId='opportunity-jurisdiction'
                        value={values.jurisdiction}
                        handleChange={(val: any) => setFieldValue('jurisdiction', val)}
                        dirty={dirty}
                        errors={{
                          jurisdiction:
                            !!errors && errors.jurisdiction ? errors.jurisdiction : null,
                        }}
                        touched={{ jurisdiction: !!touched && !!touched.jurisdiction }}
                      />
                    </div>
                    <div className='field-container'>
                      <div className='input-label'>Currency Code *</div>
                      <SelectDropdown
                        name='currency_code'
                        id='currencyCodeDropdownId'
                        formId=''
                        placeholder='Select currency'
                        list={[...OPPORTUNITY_CURRENCY_OPTIONS]}
                        dataTestId='opportunity-currency-code'
                        value={values.currency_code}
                        handleChange={(val: string) => setFieldValue('currency_code', val)}
                        dirty={dirty}
                        errors={{
                          currency_code:
                            !!errors && errors.currency_code ? errors.currency_code : null,
                        }}
                        touched={{
                          currency_code: !!touched && !!touched.currency_code,
                        }}
                      />
                    </div>
                    <div className='field-container'>
                      <Input
                        name='underlying_fund'
                        labelText='Underlying Fund'
                        dataTestId='opportunity-underlying-fund'
                        placeholder='Underlying Fund'
                        type='text'
                        value={values.metadata?.underlying_fund ?? ''}
                        handleChange={(e) =>
                          setFieldValue('metadata.underlying_fund', e.target.value)
                        }
                        dirty={dirty}
                        errors={{
                          underlying_fund:
                            !!errors && errors.metadata?.underlying_fund
                              ? errors.metadata?.underlying_fund
                              : null,
                        }}
                        touched={{
                          underlying_fund: Boolean(getIn(touched, 'metadata.underlying_fund')),
                        }}
                      />
                    </div>
                    <div className='field-container'>
                      <Input
                        name='securities_offering'
                        labelText='Securities Offering'
                        dataTestId='opportunity-securities-offering'
                        placeholder='Type of instrument'
                        type='text'
                        value={values.metadata?.securities_offering ?? ''}
                        handleChange={(e) =>
                          setFieldValue('metadata.securities_offering', e.target.value)
                        }
                        dirty={dirty}
                        errors={{
                          securities_offering:
                            !!errors && errors.metadata?.securities_offering
                              ? errors.metadata?.securities_offering
                              : null,
                        }}
                        touched={{
                          securities_offering: Boolean(
                            getIn(touched, 'metadata.securities_offering'),
                          ),
                        }}
                      />
                      <p className='opportunity-form__hint'>
                        The exact entry will be mentioned in the allotment confirmation letter.
                      </p>
                    </div>
                    <div className='field-container'>
                      <div className='input-label'>Reference Valuation Currency</div>
                      <SelectDropdown
                        name='reference_valuation_currency'
                        id='referenceValuationCurrencyDropdownId'
                        formId=''
                        placeholder='Select currency'
                        list={[{ text: 'None', value: '' }, ...OPPORTUNITY_CURRENCY_OPTIONS]}
                        dataTestId='opportunity-reference-valuation-currency'
                        value={values.metadata?.reference_valuation?.currency ?? ''}
                        handleChange={(val: string) => {
                          const currentAmount = values.metadata?.reference_valuation?.amount
                          if (!val && (currentAmount === null || currentAmount === undefined)) {
                            setFieldValue('metadata.reference_valuation', null)
                            return
                          }
                          setFieldValue('metadata.reference_valuation', {
                            currency: val,
                            amount: currentAmount ?? null,
                          })
                        }}
                        dirty={dirty}
                        errors={{
                          reference_valuation_currency:
                            (typeof errors.metadata?.reference_valuation === 'string'
                              ? errors.metadata.reference_valuation
                              : (
                                  errors.metadata?.reference_valuation as
                                    | { currency?: string }
                                    | undefined
                                )?.currency) ?? null,
                        }}
                        touched={{
                          reference_valuation_currency: Boolean(
                            getIn(touched, 'metadata.reference_valuation.currency') ||
                              getIn(touched, 'metadata.reference_valuation'),
                          ),
                        }}
                      />
                    </div>
                    <div className='field-container'>
                      <Input
                        name='reference_valuation_amount'
                        labelText='Reference Valuation Amount'
                        dataTestId='opportunity-reference-valuation-amount'
                        placeholder='Amount (same unit as ticket size)'
                        type='number'
                        value={
                          values.metadata?.reference_valuation?.amount !== undefined &&
                          values.metadata?.reference_valuation?.amount !== null
                            ? String(values.metadata.reference_valuation.amount)
                            : ''
                        }
                        handleChange={(e) => {
                          const raw = e.target.value
                          const currentCurrency =
                            values.metadata?.reference_valuation?.currency ?? ''
                          if (raw === '' && !currentCurrency) {
                            setFieldValue('metadata.reference_valuation', null)
                            return
                          }
                          setFieldValue('metadata.reference_valuation', {
                            currency: currentCurrency,
                            amount: raw === '' ? null : Number(raw),
                          })
                        }}
                        dirty={dirty}
                        errors={{
                          reference_valuation_amount:
                            (typeof errors.metadata?.reference_valuation === 'string'
                              ? errors.metadata.reference_valuation
                              : (
                                  errors.metadata?.reference_valuation as
                                    | { amount?: string }
                                    | undefined
                                )?.amount) ?? null,
                        }}
                        touched={{
                          reference_valuation_amount: Boolean(
                            getIn(touched, 'metadata.reference_valuation.amount') ||
                              getIn(touched, 'metadata.reference_valuation'),
                          ),
                        }}
                      />
                      <p className='opportunity-form__hint'>
                        Amount in major currency units (e.g. 25000000 for AED 25,000,000). Optional.
                      </p>
                    </div>
                  </div>
                </section>

                <section className='form-section'>
                  {/* Asset Class Specific Fields */}
                  {(values.asset_class === 'Trade Finance' ||
                    values.asset_class === 'Private Credit') && (
                    <>
                      <H6 classes={['mt-28', 'mb-32']} text={`${values.asset_class} Details`} />
                      <Line />

                      <div className='form-grid form-grid--cols-3'>
                        <div className='field-container'>
                          <div className='input-label'>Payout Frequency *</div>
                          <SelectDropdown
                            name='payout_frequency'
                            dataTestId='opportunity-payout-frequency'
                            list={[
                              { text: 'Click To Select', value: '' },
                              { text: 'Monthly', value: 'monthly' },
                              { text: 'Quarterly', value: 'quarterly' },
                              { text: 'Semi-Annual', value: 'semi_annual' },
                              { text: 'Annual', value: 'annual' },
                            ]}
                            value={
                              values.payout_frequency ? String(values.payout_frequency) : undefined
                            }
                            handleChange={(val: any) => {
                              setFieldValue(
                                'payout_frequency',
                                val === ''
                                  ? null
                                  : (val as 'monthly' | 'quarterly' | 'semi_annual' | 'annual'),
                              )
                            }}
                            dirty={dirty}
                            errors={{
                              payout_frequency:
                                !!errors && errors.payout_frequency
                                  ? errors.payout_frequency
                                  : null,
                            }}
                            touched={{
                              payout_frequency: !!touched && !!touched.payout_frequency,
                            }}
                          />
                        </div>
                        <div className='field-container'>
                          <Input
                            name='investment_period_in_days'
                            labelText='Investment Period (in Days) *'
                            dataTestId='opportunity-investment-period'
                            placeholder='Investment Period in Days'
                            type='number'
                            required
                            value={
                              values.investment_period_in_days !== undefined &&
                              values.investment_period_in_days !== null
                                ? values.investment_period_in_days.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('investment_period_in_days', value)
                            }}
                            dirty={dirty}
                            errors={{
                              investment_period_in_days:
                                !!errors && errors.investment_period_in_days
                                  ? errors.investment_period_in_days
                                  : null,
                            }}
                            touched={{
                              investment_period_in_days:
                                !!touched && !!touched.investment_period_in_days,
                            }}
                          />
                        </div>
                      </div>
                      <div className='form-grid form-grid--cols-3'>
                        <div className='field-container'>
                          <Input
                            name='interest_charged_by_credibila_to_opportunity_percent'
                            labelText={`Interest Charged by Credibila to ${values.opportunity_title} (%) *`}
                            dataTestId='opportunity-interest-charged-credibila'
                            placeholder={`Interest Charged by Credibila to ${values.opportunity_title} (%) *`}
                            type='number'
                            required
                            value={
                              values.interest_charged_by_credibila_to_opportunity_percent !==
                                undefined &&
                              values.interest_charged_by_credibila_to_opportunity_percent !== null
                                ? values.interest_charged_by_credibila_to_opportunity_percent.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue(
                                'interest_charged_by_credibila_to_opportunity_percent',
                                value,
                              )
                            }}
                            dirty={dirty}
                            errors={{
                              interest_charged_by_credibila_to_opportunity_percent:
                                !!errors &&
                                errors.interest_charged_by_credibila_to_opportunity_percent
                                  ? errors.interest_charged_by_credibila_to_opportunity_percent
                                  : null,
                            }}
                            touched={{
                              interest_charged_by_credibila_to_opportunity_percent:
                                !!touched &&
                                !!touched.interest_charged_by_credibila_to_opportunity_percent,
                            }}
                          />
                        </div>
                        <div className='field-container'>
                          <Input
                            name='expected_spread_percent'
                            labelText='Expected Spread (%) *'
                            dataTestId='opportunity-expected-spread'
                            placeholder='Expected Spread (%)'
                            type='number'
                            required
                            value={
                              values.expected_spread_percent !== undefined &&
                              values.expected_spread_percent !== null
                                ? values.expected_spread_percent.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('expected_spread_percent', value)
                            }}
                            dirty={dirty}
                            errors={{
                              expected_spread_percent:
                                !!errors && errors.expected_spread_percent
                                  ? errors.expected_spread_percent
                                  : null,
                            }}
                            touched={{
                              expected_spread_percent:
                                !!touched && !!touched.expected_spread_percent,
                            }}
                          />
                        </div>
                      </div>

                      <div className='form-grid form-grid--cols-3'>
                        <div className='field-container'>
                          <Input
                            name='days_from_withdraw_request_to_payment'
                            labelText='Number of days from Withdraw Request to Payment *'
                            dataTestId='opportunity-withdraw-request-days'
                            placeholder='Days from Withdraw Request to Payment'
                            type='number'
                            required
                            value={
                              values.days_from_withdraw_request_to_payment !== undefined &&
                              values.days_from_withdraw_request_to_payment !== null
                                ? values.days_from_withdraw_request_to_payment.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('days_from_withdraw_request_to_payment', value)
                            }}
                            dirty={dirty}
                            errors={{
                              days_from_withdraw_request_to_payment:
                                !!errors && errors.days_from_withdraw_request_to_payment
                                  ? errors.days_from_withdraw_request_to_payment
                                  : null,
                            }}
                            touched={{
                              days_from_withdraw_request_to_payment:
                                !!touched && !!touched.days_from_withdraw_request_to_payment,
                            }}
                          />
                        </div>
                        <div className='field-container'>
                          <Input
                            name='exit_load_percent'
                            labelText='Exit Load (%) *'
                            dataTestId='opportunity-exit-load'
                            placeholder='Exit Load (%)'
                            type='number'
                            required
                            value={
                              values.exit_load_percent !== undefined &&
                              values.exit_load_percent !== null
                                ? values.exit_load_percent.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('exit_load_percent', value)
                            }}
                            dirty={dirty}
                            errors={{
                              exit_load_percent:
                                !!errors && errors.exit_load_percent
                                  ? errors.exit_load_percent
                                  : null,
                            }}
                            touched={{
                              exit_load_percent: !!touched && !!touched.exit_load_percent,
                            }}
                          />
                        </div>
                      </div>
                      <div className='form-grid form-grid--full'>
                        <div className='field-container'>
                          <Input
                            name='profit_sharing_to_credibila_percent'
                            labelText='Profit Sharing to Credibila (%) *'
                            dataTestId='opportunity-profit-sharing-credibila'
                            placeholder='Profit Sharing to Credibila (%)'
                            type='number'
                            required
                            value={
                              values.profit_sharing_to_credibila_percent !== undefined &&
                              values.profit_sharing_to_credibila_percent !== null
                                ? values.profit_sharing_to_credibila_percent.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('profit_sharing_to_credibila_percent', value)
                            }}
                            dirty={dirty}
                            errors={{
                              profit_sharing_to_credibila_percent:
                                !!errors && errors.profit_sharing_to_credibila_percent
                                  ? errors.profit_sharing_to_credibila_percent
                                  : null,
                            }}
                            touched={{
                              profit_sharing_to_credibila_percent:
                                !!touched && !!touched.profit_sharing_to_credibila_percent,
                            }}
                          />
                        </div>
                      </div>

                      <div className='form-grid form-grid--cols-3'>
                        <div className='field-container'>
                          <div className='input-label'>Has Lock-in Period *</div>
                          <SelectDropdown
                            name='has_lock_in_period'
                            id='hasLockInPeriodId'
                            formId=''
                            list={[
                              { text: 'Click To Select', value: '' },
                              { text: 'Yes', value: 'true' },
                              { text: 'No', value: 'false' },
                            ]}
                            dataTestId='opportunity-has-lock-in-period'
                            value={values.has_lock_in_period ? 'true' : 'false'}
                            handleChange={(val: any) => {
                              setFieldValue('has_lock_in_period', val === 'true')
                              if (val === 'false') {
                                setFieldValue('lock_in_period_in_days', null)
                              }
                            }}
                            dirty={dirty}
                            errors={{
                              has_lock_in_period:
                                !!errors && errors.has_lock_in_period
                                  ? errors.has_lock_in_period
                                  : null,
                            }}
                            touched={{
                              has_lock_in_period: !!touched && !!touched.has_lock_in_period,
                            }}
                          />
                        </div>
                      </div>
                      {values.has_lock_in_period ? (
                        <div className='form-grid form-grid--full'>
                          <div className='field-container'>
                            <Input
                              name='lock_in_period_in_days'
                              labelText='Lock-in Period (Days) *'
                              dataTestId='opportunity-lock-in-period-days'
                              placeholder='Lock-in Period in Days'
                              type='number'
                              required
                              value={
                                values.lock_in_period_in_days !== undefined &&
                                values.lock_in_period_in_days !== null
                                  ? values.lock_in_period_in_days.toString()
                                  : ''
                              }
                              handleChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value)
                                setFieldValue('lock_in_period_in_days', value)
                              }}
                              dirty={dirty}
                              errors={{
                                lock_in_period_in_days:
                                  !!errors && errors.lock_in_period_in_days
                                    ? errors.lock_in_period_in_days
                                    : null,
                              }}
                              touched={{
                                lock_in_period_in_days:
                                  !!touched && !!touched.lock_in_period_in_days,
                              }}
                            />
                          </div>
                        </div>
                      ) : null}
                      <div className='form-grid form-grid--full'>
                        <div className='field-container'>
                          <div className='input-label'>Early Redemption Allowed</div>
                          <SelectDropdown
                            name='early_redemption_allowed'
                            id='earlyRedemptionAllowed'
                            list={[
                              { text: 'Click To Select', value: '' },
                              { text: 'Yes', value: 'true' },
                              { text: 'No', value: 'false' },
                            ]}
                            dataTestId='opportunity-early-redemption-allowed'
                            value={values.early_redemption_allowed ? 'true' : 'false'}
                            handleChange={(val: any) => {
                              const boolVal = val === 'true'
                              setFieldValue('early_redemption_allowed', boolVal)
                              if (!boolVal) {
                                setFieldValue('redemption_notice_in_days', null)
                                setFieldValue('redemption_fee_percent', null)
                                setFieldValue('redemption_period_in_days', null)
                              }
                            }}
                            dirty={dirty}
                            errors={{
                              early_redemption_allowed:
                                !!errors && errors.early_redemption_allowed
                                  ? errors.early_redemption_allowed
                                  : null,
                            }}
                            touched={{
                              early_redemption_allowed:
                                !!touched && !!touched.early_redemption_allowed,
                            }}
                          />
                        </div>
                      </div>

                      {values.early_redemption_allowed && (
                        <>
                          <div className='form-grid form-grid--cols-3'>
                            <div className='field-container'>
                              <Input
                                name='redemption_notice_in_days'
                                labelText='Redemption Notice (Days)'
                                dataTestId='opportunity-redemption-notice-days'
                                placeholder='Enter notice days'
                                type='number'
                                required
                                value={values.redemption_notice_in_days?.toString() ?? ''}
                                handleChange={(e) => {
                                  const val = e.target.value === '' ? null : Number(e.target.value)
                                  setFieldValue('redemption_notice_in_days', val)
                                }}
                                dirty={dirty}
                                errors={{
                                  redemption_notice_in_days:
                                    !!errors && errors.redemption_notice_in_days
                                      ? errors.redemption_notice_in_days
                                      : null,
                                }}
                                touched={{
                                  redemption_notice_in_days:
                                    !!touched && !!touched.redemption_notice_in_days,
                                }}
                              />
                            </div>

                            <div className='field-container'>
                              <Input
                                name='redemption_fee_percent'
                                labelText='Redemption Fee (%)'
                                dataTestId='opportunity-redemption-fee-percent'
                                placeholder='Enter fee %'
                                type='number'
                                required
                                value={values.redemption_fee_percent?.toString() ?? ''}
                                handleChange={(e) => {
                                  const val = e.target.value === '' ? null : Number(e.target.value)
                                  setFieldValue('redemption_fee_percent', val)
                                }}
                                dirty={dirty}
                                errors={{
                                  redemption_fee_percent:
                                    !!errors && errors.redemption_fee_percent
                                      ? errors.redemption_fee_percent
                                      : null,
                                }}
                                touched={{
                                  redemption_fee_percent:
                                    !!touched && !!touched.redemption_fee_percent,
                                }}
                              />
                            </div>
                          </div>
                          <div className='form-grid form-grid--cols-3'>
                            <div className='field-container'>
                              <div className='input-label'>Redemption Period</div>
                              <SelectDropdown
                                name='redemption_period'
                                dataTestId='opportunity-redemption-period'
                                list={[
                                  { text: 'Click To Select', value: '' },
                                  { text: 'Monthly', value: 'monthly' },
                                  { text: 'Quarterly', value: 'quarterly' },
                                  { text: 'Semi-Annual', value: 'semi_annual' },
                                  { text: 'Annual', value: 'annual' },
                                ]}
                                value={
                                  values.redemption_period
                                    ? String(values.redemption_period)
                                    : undefined
                                }
                                handleChange={(val: any) => {
                                  setFieldValue(
                                    'redemption_period',
                                    val === ''
                                      ? null
                                      : (val as 'monthly' | 'quarterly' | 'semi_annual' | 'annual'),
                                  )
                                }}
                                dirty={dirty}
                                errors={{
                                  redemption_period:
                                    !!errors && errors.redemption_period
                                      ? errors.redemption_period
                                      : null,
                                }}
                                touched={{
                                  redemption_period: !!touched && !!touched.redemption_period,
                                }}
                              />
                            </div>

                            <div className='field-container'>
                              <Input
                                name='redemption_gate_percent'
                                labelText='Redemption Gate (%)'
                                dataTestId='opportunity-redemption-gate-percent'
                                placeholder='Enter %'
                                type='number'
                                required
                                value={values.redemption_gate_percent?.toString() ?? ''}
                                handleChange={(e) => {
                                  const val = e.target.value === '' ? null : Number(e.target.value)
                                  setFieldValue('redemption_gate_percent', val)
                                }}
                                dirty={dirty}
                                errors={{
                                  redemption_gate_percent:
                                    !!errors && errors.redemption_gate_percent
                                      ? errors.redemption_gate_percent
                                      : null,
                                }}
                                touched={{
                                  redemption_gate_percent:
                                    !!touched && !!touched.redemption_gate_percent,
                                }}
                              />
                            </div>
                          </div>
                        </>
                      )}
                      <Line />
                    </>
                  )}

                  {/* Mutual Fund Specific Fields */}
                  {values.asset_class === 'Mutual Fund' && (
                    <>
                      <H6 classes={['mt-28', 'mb-32']} text='Mutual Fund Details' />
                      <Line />

                      <div className='form-grid form-grid--full'>
                        <div className='field-container'>
                          <Input
                            name='profit_sharing_to_credibila_percent'
                            labelText='Profit Sharing to Credibila (%) *'
                            dataTestId='opportunity-profit-sharing-credibila'
                            placeholder='Profit Sharing to Credibila (%)'
                            type='number'
                            required
                            value={
                              values.profit_sharing_to_credibila_percent !== undefined &&
                              values.profit_sharing_to_credibila_percent !== null
                                ? values.profit_sharing_to_credibila_percent.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('profit_sharing_to_credibila_percent', value)
                            }}
                            dirty={dirty}
                            errors={{
                              profit_sharing_to_credibila_percent:
                                !!errors && errors.profit_sharing_to_credibila_percent
                                  ? errors.profit_sharing_to_credibila_percent
                                  : null,
                            }}
                            touched={{
                              profit_sharing_to_credibila_percent:
                                !!touched && !!touched.profit_sharing_to_credibila_percent,
                            }}
                          />
                        </div>
                      </div>

                      {values.has_lock_in_period ? (
                        <div className='form-grid form-grid--full'>
                          <div className='field-container'>
                            <Input
                              name='lock_in_period_in_days'
                              labelText='Lock-in Period (Days) *'
                              dataTestId='opportunity-lock-in-period-days'
                              placeholder='Lock-in Period in Days'
                              type='number'
                              required
                              value={
                                values.lock_in_period_in_days !== undefined &&
                                values.lock_in_period_in_days !== null
                                  ? values.lock_in_period_in_days.toString()
                                  : ''
                              }
                              handleChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value)
                                setFieldValue('lock_in_period_in_days', value)
                              }}
                              dirty={dirty}
                              errors={{
                                lock_in_period_in_days:
                                  !!errors && errors.lock_in_period_in_days
                                    ? errors.lock_in_period_in_days
                                    : null,
                              }}
                              touched={{
                                lock_in_period_in_days:
                                  !!touched && !!touched.lock_in_period_in_days,
                              }}
                            />
                          </div>
                        </div>
                      ) : null}
                      <Line />
                    </>
                  )}

                  {/* Listed Equity Specific Fields */}
                  {values.asset_class === 'Listed Equity' && (
                    <>
                      <H6 classes={['mt-28', 'mb-32']} text='Listed Equity Details' />
                      <Line />

                      <div className='form-grid form-grid--cols-3'>
                        <div className='field-container'>
                          <Input
                            name='total_shares'
                            labelText='Total Shares'
                            dataTestId='opportunity-total-shares'
                            placeholder='Total number of shares'
                            type='number'
                            value={
                              values.asset_class_data?.total_shares !== undefined &&
                              values.asset_class_data?.total_shares !== null
                                ? values.asset_class_data.total_shares.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('asset_class_data.total_shares', value)
                            }}
                            dirty={dirty}
                            errors={{
                              total_shares:
                                !!errors &&
                                errors.asset_class_data &&
                                typeof errors.asset_class_data === 'object' &&
                                'total_shares' in errors.asset_class_data
                                  ? (errors.asset_class_data as any).total_shares
                                  : null,
                            }}
                            touched={{
                              total_shares: !!(
                                touched &&
                                touched.asset_class_data &&
                                typeof touched.asset_class_data === 'object' &&
                                'total_shares' in touched.asset_class_data
                              ),
                            }}
                          />
                        </div>
                        <div className='field-container'>
                          <Input
                            name='lock_in_period_in_days'
                            labelText='Lock-in Period (Days) *'
                            dataTestId='opportunity-lock-in-period-days'
                            placeholder='Lock-in Period in Days'
                            type='number'
                            required
                            value={
                              values.lock_in_period_in_days !== undefined &&
                              values.lock_in_period_in_days !== null
                                ? values.lock_in_period_in_days.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('lock_in_period_in_days', value)
                            }}
                            dirty={dirty}
                            errors={{
                              lock_in_period_in_days:
                                !!errors && errors.lock_in_period_in_days
                                  ? errors.lock_in_period_in_days
                                  : null,
                            }}
                            touched={{
                              lock_in_period_in_days: !!touched && !!touched.lock_in_period_in_days,
                            }}
                          />
                        </div>
                        <div className='field-container'>
                          <Input
                            name='profit_sharing_to_credibila_percent'
                            labelText='Profit Sharing to Credibila (%) *'
                            dataTestId='opportunity-profit-sharing-credibila'
                            placeholder='Profit Sharing to Credibila (%)'
                            type='number'
                            required
                            value={
                              values.profit_sharing_to_credibila_percent !== undefined &&
                              values.profit_sharing_to_credibila_percent !== null
                                ? values.profit_sharing_to_credibila_percent.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('profit_sharing_to_credibila_percent', value)
                            }}
                            dirty={dirty}
                            errors={{
                              profit_sharing_to_credibila_percent:
                                !!errors && errors.profit_sharing_to_credibila_percent
                                  ? errors.profit_sharing_to_credibila_percent
                                  : null,
                            }}
                            touched={{
                              profit_sharing_to_credibila_percent:
                                !!touched && !!touched.profit_sharing_to_credibila_percent,
                            }}
                          />
                        </div>
                      </div>

                      <div className='form-grid form-grid--cols-3'>
                        <div className='field-container'>
                          <Dropdown
                            labelText='Exchange'
                            name='exchange'
                            value={exchange}
                            handleChange={(e) => setExchange(e.target.value)}
                            options={[
                              { label: 'NSE', value: 'NSE' },
                              { label: 'BSE', value: 'BSE' },
                            ]}
                          />
                        </div>
                        {exchange === 'NSE' && (
                          <div className='field-container'>
                            <Input
                              name='stock_nse'
                              dataTestId='stock_nse'
                              labelText='NSE Ticker'
                              placeholder='Exchange:Tikcer Example NSE:TCS'
                              type='text'
                              value={values.metadata?.stock_nse ?? ''}
                              handleChange={(e) =>
                                setFieldValue('metadata.stock_nse', e.target.value)
                              }
                            />
                          </div>
                        )}

                        {exchange === 'BSE' && (
                          <div className='field-container'>
                            <Input
                              name='stock_bse'
                              dataTestId='stock_bse'
                              labelText='BSE Ticker'
                              placeholder='Tikcer:Exchange Example 12345:BOM'
                              type='text'
                              value={values.metadata?.stock_bse ?? ''}
                              handleChange={(e) =>
                                setFieldValue('metadata.stock_bse', e.target.value)
                              }
                            />
                          </div>
                        )}
                      </div>
                      <Line />
                    </>
                  )}

                  {/* Private Equity Specific Fields */}
                  {values.asset_class === 'Private Equity' && (
                    <>
                      <H6 classes={['mt-28', 'mb-32']} text='Private Equity Details' />
                      <Line />

                      <div className='form-grid form-grid--full'>
                        <div className='field-container'>
                          <Input
                            name='total_shares'
                            labelText='Total Shares'
                            dataTestId='opportunity-total-shares'
                            placeholder='Total number of shares'
                            type='number'
                            value={
                              values.asset_class_data?.total_shares !== undefined &&
                              values.asset_class_data?.total_shares !== null
                                ? values.asset_class_data.total_shares.toString()
                                : ''
                            }
                            handleChange={(e) => {
                              const value = e.target.value === '' ? null : Number(e.target.value)
                              setFieldValue('asset_class_data.total_shares', value)
                            }}
                            dirty={dirty}
                            errors={{
                              total_shares:
                                !!errors &&
                                errors.asset_class_data &&
                                typeof errors.asset_class_data === 'object' &&
                                'total_shares' in errors.asset_class_data
                                  ? (errors.asset_class_data as any).total_shares
                                  : null,
                            }}
                            touched={{
                              total_shares: !!(
                                touched &&
                                touched.asset_class_data &&
                                typeof touched.asset_class_data === 'object' &&
                                'total_shares' in touched.asset_class_data
                              ),
                            }}
                          />
                        </div>
                      </div>

                      <div className='form-grid form-grid--full'>
                        <div className='field-container'>
                          <CheckboxInput
                            name='has_lock_in_period'
                            labelText='Has Lock-in Period'
                            dataTestId='opportunity-has-lock-in-period'
                            checked={Boolean(values.has_lock_in_period)}
                            handleChange={(e) =>
                              setFieldValue('has_lock_in_period', e.target.checked)
                            }
                            dirty={dirty}
                          />
                        </div>
                      </div>

                      {values.has_lock_in_period ? (
                        <div className='form-grid form-grid--full'>
                          <div className='field-container'>
                            <Input
                              name='lock_in_period_in_days'
                              labelText='Lock-in Period (Days) *'
                              dataTestId='opportunity-lock-in-period-days'
                              placeholder='Lock-in Period in Days'
                              type='number'
                              required
                              value={
                                values.lock_in_period_in_days !== undefined &&
                                values.lock_in_period_in_days !== null
                                  ? values.lock_in_period_in_days.toString()
                                  : ''
                              }
                              handleChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value)
                                setFieldValue('lock_in_period_in_days', value)
                              }}
                              dirty={dirty}
                              errors={{
                                lock_in_period_in_days:
                                  !!errors && errors.lock_in_period_in_days
                                    ? errors.lock_in_period_in_days
                                    : null,
                              }}
                              touched={{
                                lock_in_period_in_days:
                                  !!touched && !!touched.lock_in_period_in_days,
                              }}
                            />
                          </div>
                        </div>
                      ) : null}
                      <Line />
                    </>
                  )}
                </section>

                <section className='form-section'>
                  <H6 classes={['mt-28', 'mb-32', 'form-section-title']} text='Deal terms' />
                  <Line />
                  <div className='form-grid form-grid--cols-3'>
                    <div className='field-container'>
                      <Input
                        name='ticket_size'
                        labelText='Minimum Amount (in USD)*'
                        dataTestId='opportunity-ticket-size'
                        placeholder='Minimum Amount'
                        type='number'
                        required
                        value={values.ticket_size.toString()}
                        handleChange={(e) => setFieldValue('ticket_size', e.target.value)}
                        dirty={dirty}
                        errors={{
                          ticket_size: !!errors && errors.ticket_size ? errors.ticket_size : null,
                        }}
                        touched={{
                          ticket_size: !!touched && !!touched.ticket_size,
                        }}
                      />
                    </div>
                    <div
                      className='field-container'
                      data-testid='opportunity-closing-date-container'
                    >
                      <div className='input-label'> Closing Date*</div>
                      <DatePicker
                        value={values.closing_date ? new Date(values.closing_date) : null}
                        format='MMMM D, YYYY'
                        required={true}
                        classes={['form-control', 'mt-8']}
                        minDate={new Date()}
                        dataTestId='closing-date'
                        changeHandler={(date) => {
                          setFieldValue('closing_date', date)
                          setFieldTouched('closing_date', true, false)
                        }}
                      />
                      {errors.closing_date && touched.closing_date && (
                        <StatusLabel
                          type={ERROR}
                          variant='basic'
                          text={String(errors.closing_date)}
                        />
                      )}
                    </div>
                    <div className='field-container'>
                      <Input
                        name='raiseGoal'
                        labelText='Raise Goal (in USD)'
                        dataTestId='opportunity-raise-goal'
                        placeholder='Total investment available to invest'
                        type='number'
                        value={
                          values.raise_goal !== undefined && values.raise_goal !== null
                            ? values.raise_goal.toString()
                            : ''
                        }
                        handleChange={(e) => {
                          const value = e.target.value === '' ? null : Number(e.target.value)
                          setFieldValue('raise_goal', value)
                        }}
                        dirty={dirty}
                        errors={{
                          raise_goal: !!errors && errors.raise_goal ? errors.raise_goal : null,
                        }}
                        touched={{ raise_goal: !!touched && !!touched.raise_goal }}
                      />
                    </div>
                  </div>
                </section>

                <section className='form-section'>
                  <H6 classes={['mt-28', 'mb-32', 'form-section-title']} text='Fixed income' />
                  <Line />
                  <div className='form-grid form-grid--full'>
                    <div className='field-container'>
                      <CheckboxInput
                        name='isFixedIncome'
                        labelText='Fixed income (enables interest payout)'
                        value={values.metadata.is_fixed_income ? 'true' : 'false'}
                        handleChange={(val) =>
                          setFieldValue('metadata.is_fixed_income', val.target.checked)
                        }
                        dataTestId='opportunity-is-fixed-income'
                      />
                    </div>
                  </div>

                  {values.metadata.is_fixed_income === true && (
                    <div className='form-grid form-grid--cols-3'>
                      <div className='field-container'>
                        <Input
                          name='investment_return'
                          labelText='Fixed income coupon rate (%)'
                          dataTestId='opportunity-investment-return'
                          placeholder='Coupon rate used for calculations'
                          type='text'
                          value={values.investment_return.toString()}
                          handleChange={(e) => setFieldValue('investment_return', e.target.value)}
                          dirty={dirty}
                          errors={{
                            investment_return:
                              !!errors && errors.investment_return
                                ? errors.investment_return
                                : null,
                          }}
                          touched={{
                            investment_return: Boolean(touched.investment_return),
                          }}
                        />
                      </div>
                      <div className='field-container'>
                        <Input
                          name='asset_fee'
                          labelText='Portfolio Service Fee(%)'
                          placeholder={'Portfolio Service Fee (%)'}
                          dataTestId='opportunity-asset-fee'
                          type='text'
                          value={values?.metadata?.asset_fee ?? ''}
                          handleChange={(e) => setFieldValue('metadata.asset_fee', e.target.value)}
                          dirty={dirty}
                          errors={{
                            asset_fee:
                              !!errors && errors.metadata?.asset_fee
                                ? errors.metadata?.asset_fee
                                : null,
                          }}
                          touched={{
                            asset_fee: Boolean(getIn(touched, 'metadata.asset_fee')),
                          }}
                        />
                      </div>
                    </div>
                  )}
                </section>

                <section className='form-section'>
                  <H6
                    classes={['mt-28', 'mb-8', 'form-section-title']}
                    text='Fees (non–fixed income)'
                  />
                  <div className='publish-warning-sub-heading mb-20'>
                    <p>
                      Target return range and investor-facing fee tiles on the detail page are
                      configured in Sanity → Fees.
                    </p>
                  </div>

                  {!values.metadata?.is_fixed_income && (
                    <div className='form-grid form-grid--cols-3'>
                      <div className='field-container'>
                        <Input
                          name='hurdle_rate'
                          labelText='Hurdle Rate(%)'
                          placeholder={'Hurdle Rate (%)'}
                          dataTestId='opportunity-hurdle-rate'
                          type='text'
                          value={values?.metadata?.hurdle_rate ?? ''}
                          handleChange={(e) =>
                            setFieldValue('metadata.hurdle_rate', e.target.value)
                          }
                          dirty={dirty}
                          errors={{
                            hurdle_rate:
                              !!errors && errors.metadata?.hurdle_rate
                                ? errors.metadata?.hurdle_rate
                                : null,
                          }}
                          touched={{
                            hurdle_rate: Boolean(getIn(touched, 'metadata.hurdle_rate')),
                          }}
                        />
                      </div>
                      <div className='field-container'>
                        <Input
                          name='carry_rate'
                          labelText='Carry Rate(%)'
                          placeholder={'Carry Rate (%)'}
                          dataTestId='opportunity-carry-rate'
                          type='text'
                          value={values?.metadata?.carry_rate ?? ''}
                          handleChange={(e) => setFieldValue('metadata.carry_rate', e.target.value)}
                          dirty={dirty}
                          errors={{
                            carry_rate:
                              !!errors && errors.metadata?.carry_rate
                                ? errors.metadata?.carry_rate
                                : null,
                          }}
                          touched={{
                            carry_rate: Boolean(getIn(touched, 'metadata.carry_rate')),
                          }}
                        />
                      </div>
                      <div className='field-container'>
                        <Input
                          name='portfolio_service_fee'
                          labelText='Portfolio Service Fee(%)'
                          placeholder={'Portfolio Service Fee (%)'}
                          dataTestId='opportunity-portfolio-service-fee'
                          type='text'
                          value={values?.metadata?.portfolio_service_fee ?? ''}
                          handleChange={(e) =>
                            setFieldValue('metadata.portfolio_service_fee', e.target.value)
                          }
                          dirty={dirty}
                          errors={{
                            portfolio_service_fee: errors?.metadata?.portfolio_service_fee ?? null,
                          }}
                          touched={{
                            portfolio_service_fee:
                              touched?.metadata?.portfolio_service_fee ?? false,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </section>

                <section className='form-section'>
                  <Line />
                  <H6
                    classes={['mt-28', 'mb-32', 'form-section-title']}
                    text='Termsheet Agreement'
                  />
                  <Line />
                  <div className='form-grid form-grid--full'>
                    <div className='field-container'>
                      <Input
                        name='termsheet_agreement_template_s3_url'
                        labelText='Termsheet Agreement Template ID'
                        placeholder={'Termsheet Agreement URL'}
                        dataTestId='opportunity-termsheet-agreement-template-url'
                        type='text'
                        value={values?.metadata?.termsheet_agreement_template_url ?? ''}
                        handleChange={(e) =>
                          setFieldValue('metadata.termsheet_agreement_template_url', e.target.value)
                        }
                        dirty={dirty}
                        errors={{
                          termsheet_agreement_template_s3_url:
                            !!errors && errors.metadata?.termsheet_agreement_template_url
                              ? errors.metadata?.termsheet_agreement_template_url
                              : null,
                        }}
                        touched={{
                          termsheet_agreement_template_s3_url: Boolean(
                            getIn(touched, 'metadata.termsheet_agreement_template_url'),
                          ),
                        }}
                      />
                    </div>
                    <DynamicFieldMappingEditor />
                  </div>
                </section>

                <section className='form-section'>
                  <H6
                    classes={['mt-28', 'mb-32', 'form-section-title']}
                    text='List card and summaries'
                  />
                  <Line />
                  <div className='form-grid form-grid--full'>
                    <div className='field-container'>
                      <TextboxInput
                        name='opportunity_summary'
                        labelText='Opportunity Summary *'
                        dataTestId='opportunity-summary'
                        // placeholder='Max 500 characters allowed'
                        required
                        value={values.opportunity_summary}
                        handleChange={(e) => setFieldValue('opportunity_summary', e.target.value)}
                        dirty={dirty}
                        errors={{
                          opportunity_summary:
                            !!errors && errors.opportunity_summary
                              ? errors.opportunity_summary
                              : null,
                        }}
                        touched={{
                          opportunity_summary: !!touched && !!touched.opportunity_summary,
                        }}
                        // infoText={`Character ${values.opportunity_summary.length} out of 500`}
                      />
                    </div>
                  </div>

                  <div className='form-grid form-grid--full mb-12'>
                    <div className='field-container'>
                      <TextboxInput
                        name='short_opportunity_summary'
                        labelText='Short Opportunity Summary *'
                        dataTestId='opportunity-short-summary'
                        placeholder='Max 255 characters allowed'
                        required
                        value={values.short_opportunity_summary}
                        handleChange={(e) =>
                          setFieldValue('short_opportunity_summary', e.target.value)
                        }
                        dirty={dirty}
                        errors={{
                          short_opportunity_summary:
                            !!errors && errors.short_opportunity_summary
                              ? errors.short_opportunity_summary
                              : null,
                        }}
                        touched={{
                          short_opportunity_summary:
                            !!touched && !!touched.short_opportunity_summary,
                        }}
                        infoText={`Character ${
                          values?.short_opportunity_summary?.length ?? 0
                        } out of 255`}
                      />
                    </div>
                  </div>

                  <div className='publish-warning-sub-heading mb-20'>
                    <p>
                      Detail-page content (hero, sections, gallery, links, news) is managed in
                      Sanity. Slug must match Opportunity Slug.
                    </p>
                  </div>

                  <H6 classes={['mt-28', 'mb-32']} text='Images' />
                  <Line />

                  <Flex
                    classes={['form-image-flex-direction', 'form-image-stack', 'mt-40']}
                    variant='space-between'
                  >
                    <Flex variant='column-center' classes={['form-image-field']}>
                      <UploadedImage published fileUrl={signedImageUrl.cover_image} id={'1'} />
                      <InputFile
                        name='projectCover'
                        dataTestId='opportunity-cover-image'
                        handleFiles={async (file: any) => {
                          if (handleFileUpload && file) {
                            const data = await handleFileUpload(file as File, 'image')
                            if (data && data.data) {
                              setFieldValue('cover_image', data.data.key)
                              const { url } = await generatePreview(data.data.key)
                              setSignedImageUrl({ ...signedImageUrl, cover_image: url })
                            } else if (data?.message) {
                              handleErrorMessage && handleErrorMessage(data?.message)
                            }
                          } else {
                            setFieldValue('cover_image', '')
                            setSignedImageUrl({ ...signedImageUrl, cover_image: '' })
                          }
                        }}
                        {...(Object.keys(uploadFilesMetaData.cover_image).length > 0
                          ? { file: uploadFilesMetaData.cover_image }
                          : {})}
                        label='Opportunity Cover'
                        dirty={dirty}
                        errors={{
                          projectCover: !!errors && errors.cover_image ? errors.cover_image : null,
                        }}
                        touched={{
                          projectCover: !!touched && !!touched.cover_image,
                        }}
                        handleDeleteFile={() => {
                          setFieldValue('cover_image', '')
                          setSignedImageUrl({ ...signedImageUrl, cover_image: '' })
                        }}
                      />
                    </Flex>
                    <Flex variant='column-center' classes={['form-image-field']}>
                      <UploadedImage
                        published
                        fileUrl={signedImageUrl.logo_image}
                        id={'company-logo-square'}
                      />
                      <InputFile
                        name='companyLogo'
                        dataTestId='opportunity-logo-image'
                        type='logo'
                        handleFiles={async (file: any) => {
                          if (handleFileUpload && file) {
                            const data = await handleFileUpload(file as File, 'logo')
                            if (data && data.data) {
                              setFieldValue('logo_image', data.data.key)
                              const { url } = await generatePreview(data.data.key)
                              setSignedImageUrl((prev) => ({ ...prev, logo_image: url }))
                            } else if (data?.message) {
                              handleErrorMessage && handleErrorMessage(data?.message)
                            }
                          } else {
                            setFieldValue('logo_image', '')
                            setSignedImageUrl((prev) => ({ ...prev, logo_image: '' }))
                          }
                        }}
                        {...(Object.keys(uploadFilesMetaData.logo_image).length > 0
                          ? { file: uploadFilesMetaData.logo_image }
                          : {})}
                        label='Company logo (square)'
                        dirty={dirty}
                        errors={{
                          companyLogo: !!errors && errors.logo_image ? errors.logo_image : null,
                        }}
                        touched={{
                          companyLogo: !!touched && !!touched.logo_image,
                        }}
                        handleDeleteFile={() => {
                          setFieldValue('logo_image', '')
                          setSignedImageUrl((prev) => ({ ...prev, logo_image: '' }))
                        }}
                      />
                      <p className='input-label form-image-note' style={{ fontWeight: 400 }}>
                        Square icon shown in small tiles (Newly added, Watchlist, Saved). PNG, at
                        least 256 px, on a plain or transparent background.
                      </p>
                    </Flex>
                    <Flex variant='column-center' classes={['form-image-field']}>
                      <UploadedImage
                        published
                        fileUrl={signedImageUrl.logo_wide_image}
                        id={'company-logo-wide'}
                      />
                      <InputFile
                        name='companyLogoWide'
                        dataTestId='opportunity-logo-wide-image'
                        type='logo'
                        handleFiles={async (file: any) => {
                          if (handleFileUpload && file) {
                            const data = await handleFileUpload(file as File, 'logo')
                            if (data && data.data) {
                              setFieldValue('logo_wide_image', data.data.key)
                              const { url } = await generatePreview(data.data.key)
                              setSignedImageUrl((prev) => ({ ...prev, logo_wide_image: url }))
                            } else if (data?.message) {
                              handleErrorMessage && handleErrorMessage(data?.message)
                            }
                          } else {
                            setFieldValue('logo_wide_image', '')
                            setSignedImageUrl((prev) => ({ ...prev, logo_wide_image: '' }))
                          }
                        }}
                        {...(Object.keys(uploadFilesMetaData.logo_wide_image).length > 0
                          ? { file: uploadFilesMetaData.logo_wide_image }
                          : {})}
                        label='Company logo (wide)'
                        dirty={dirty}
                        errors={{
                          companyLogoWide:
                            !!errors && errors.logo_wide_image ? errors.logo_wide_image : null,
                        }}
                        touched={{
                          companyLogoWide: !!touched && !!touched.logo_wide_image,
                        }}
                        handleDeleteFile={() => {
                          setFieldValue('logo_wide_image', '')
                          setSignedImageUrl((prev) => ({ ...prev, logo_wide_image: '' }))
                        }}
                      />
                      <p className='input-label form-image-note' style={{ fontWeight: 400 }}>
                        Wide wordmark shown on Discover cards. PNG, about 3:1, at least 256 px, on a
                        plain or transparent background.
                      </p>
                    </Flex>
                  </Flex>
                </section>

                <section className='form-section'>
                  <H6 classes={['mt-28', 'mb-32', 'form-section-title']} text='Publishing' />
                  <Line />
                  <div className='form-grid form-grid--full'>
                    <div className='field-container'>
                      <div className='input-label'>Visibility *</div>
                      <SelectDropdown
                        name='status'
                        id='visibilityDropdownId'
                        formId=''
                        list={OPPORTUNITY_VISIBILITY_OPTIONS}
                        dataTestId='opportunity-visibility-select'
                        value={values.status}
                        handleChange={(val: string) =>
                          setFieldValue('status', val as PublishingStatus)
                        }
                        dirty={dirty}
                        errors={{
                          status: !!errors && errors.status ? errors.status : null,
                        }}
                        touched={{ status: !!touched && !!touched.status }}
                      />
                      <div
                        className='publish-warning-sub-heading mb-20'
                        style={{ marginTop: '8px' }}
                      >
                        <p>
                          <strong>Published:</strong> Visible to all users in listings and search
                          results.
                          <br />
                          <strong>Unpublished:</strong> Removed from the website.
                          <br />
                          <strong>Private (Link-view):</strong> Hidden from listings, accessible
                          only via unique link.
                          <br />
                          <strong>Closed for Investment:</strong> Moved to past opportunities.
                        </p>
                      </div>
                    </div>
                  </div>

                  {values.status === 'LINK_ONLY' && values.access_token && values.opportunity_slug && (
                    <Flex classes={['mb-20']} variant='column'>
                      <div className='input-label'>Shareable Link</div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Input
                          name='shareableLink'
                          type='text'
                          value={`${window.location.origin}/opportunity/${values.opportunity_slug}?access=${values.access_token}`}
                          disabled
                          dataTestId='opportunity-shareable-link'
                        />
                        <Button
                          variant='secondary-btn'
                          text='Copy'
                          dataTestId='opportunity-copy-link-button'
                          handleClick={() => {
                            void copyLinkToClipboard(
                              `${window.location.origin}/opportunity/${values.opportunity_slug}?access=${values.access_token}`,
                            )
                          }}
                        />
                      </div>
                      <div className='publish-warning-sub-heading' style={{ marginTop: '8px' }}>
                        <p>
                          Share this link with select clients to grant them access to this
                          opportunity. Anyone with this link can view the opportunity details.
                        </p>
                      </div>
                    </Flex>
                  )}

                  <div className='form-grid form-grid--full mt-12'>
                    <div className='field-container'>
                      <CheckboxInput
                        name='isFeatured'
                        labelText='Featured opportunity'
                        value={values.is_featured ? 'true' : 'false'}
                        handleChange={(val) => setFieldValue('is_featured', val.target.checked)}
                        dataTestId='opportunity-is-featured'
                      />
                    </div>
                    <div className='field-container'>
                      <CheckboxInput
                        name='isPublic'
                        labelText='Visible to guests (without login)'
                        value={values.is_public ? 'true' : 'false'}
                        handleChange={(val) => setFieldValue('is_public', val.target.checked)}
                        dataTestId='opportunity-is-public'
                      />
                    </div>
                    <div className='publish-warning-sub-heading form-grid--full'>
                      <p>
                        When unchecked, only logged-in investors and admins can discover this
                        opportunity in listings. This is separate from Visibility status above.
                      </p>
                    </div>
                    <div className='field-container'>
                      <CheckboxInput
                        name='isEligibleForCustomTerms'
                        labelText='Eligible for custom client terms'
                        value={values.metadata.is_eligible_for_custom_terms ? 'true' : 'false'}
                        handleChange={(val) =>
                          setFieldValue('metadata.is_eligible_for_custom_terms', val.target.checked)
                        }
                        dataTestId='opportunity-is-eligible-custom-terms'
                      />
                    </div>
                    <div className='field-container'>
                      <CheckboxInput
                        name='requestAllocationEnabled'
                        labelText='Enable Request Allocation CTA'
                        value={values.metadata.request_allocation_enabled ? 'true' : 'false'}
                        handleChange={(val) =>
                          setFieldValue('metadata.request_allocation_enabled', val.target.checked)
                        }
                        dataTestId='opportunity-request-allocation-enabled'
                      />
                    </div>
                    <div className='publish-warning-sub-heading form-grid--full'>
                      <p>
                        When checked, clients see &quot;Request Allocation&quot; instead of
                        &quot;Proceed with Investment&quot;. Unchecked keeps the normal invest flow.
                      </p>
                    </div>
                    <div className='field-container'>
                      <CheckboxInput
                        name='highlightEnabled'
                        labelText='Highlight on client opportunities page'
                        value={values.metadata.highlight_enabled ? 'true' : 'false'}
                        handleChange={(val) =>
                          setFieldValue('metadata.highlight_enabled', val.target.checked)
                        }
                        dataTestId='opportunity-highlight-enabled'
                      />
                    </div>
                    <div className='publish-warning-sub-heading form-grid--full'>
                      <p>
                        When checked, this opportunity is spotlighted with a hero banner and a
                        &quot;Live on Credibila&quot; card on the client opportunities page. Only
                        the first highlighted opportunity is shown in the banner.
                      </p>
                    </div>
                    <div className='form-grid form-grid--cols-2'>
                      <div className='field-container'>
                        <Input
                          name='price_per_share'
                          labelText='Price per share (USD)'
                          placeholder='Price per share (USD)'
                          dataTestId='opportunity-price-per-share'
                          type='text'
                          value={values?.metadata?.price_per_share ?? ''}
                          handleChange={(e) =>
                            setFieldValue('metadata.price_per_share', e.target.value)
                          }
                          dirty={dirty}
                          errors={{
                            price_per_share:
                              !!errors && errors.metadata?.price_per_share
                                ? errors.metadata?.price_per_share
                                : null,
                          }}
                          touched={{
                            price_per_share: Boolean(getIn(touched, 'metadata.price_per_share')),
                          }}
                        />
                      </div>
                      <div className='field-container'>
                        <Input
                          name='sales_fee_percent'
                          labelText='Upfront Fees (%)'
                          placeholder='Upfront Fees (%)'
                          dataTestId='opportunity-sales-fee-percent'
                          type='text'
                          value={values?.metadata?.sales_fee_percent ?? ''}
                          handleChange={(e) =>
                            setFieldValue('metadata.sales_fee_percent', e.target.value)
                          }
                          dirty={dirty}
                          errors={{
                            sales_fee_percent:
                              !!errors && errors.metadata?.sales_fee_percent
                                ? errors.metadata?.sales_fee_percent
                                : null,
                          }}
                          touched={{
                            sales_fee_percent: Boolean(
                              getIn(touched, 'metadata.sales_fee_percent'),
                            ),
                          }}
                        />
                      </div>
                    </div>
                    <div className='publish-warning-sub-heading form-grid--full'>
                      <p>
                        When a price per share is set, the client investment step shows the share
                        calculator (invest by amount or number of shares) and applies the sales fee
                        to the investment amount. Leave empty to keep the standard amount-only flow.
                      </p>
                    </div>
                    <div className='field-container'>
                      <CheckboxInput
                        name='transferPageEnabled'
                        labelText='Enable transfer page in investment journey'
                        value={values.transfer_page_enabled ? 'true' : 'false'}
                        handleChange={(val) =>
                          setFieldValue('transfer_page_enabled', val.target.checked)
                        }
                        dataTestId='opportunity-transfer-page-enabled'
                      />
                    </div>
                    <div className='field-container'>
                      <CheckboxInput
                        name='secondaryTradingEnabled'
                        labelText='Enable secondary market trading for this opportunity'
                        value={values.secondary_trading_enabled ? 'true' : 'false'}
                        handleChange={(val) =>
                          setFieldValue('secondary_trading_enabled', val.target.checked)
                        }
                        dataTestId='opportunity-secondary-trading-enabled'
                      />
                    </div>
                    <div className='field-container'>
                      <CheckboxInput
                        name='marketingSignupEnabled'
                        labelText='Enable marketing signup landing page'
                        value={values.marketing_signup_enabled ? 'true' : 'false'}
                        handleChange={(val) =>
                          setFieldValue('marketing_signup_enabled', val.target.checked)
                        }
                        dataTestId='opportunity-marketing-signup-enabled'
                      />
                    </div>
                    {values.marketing_signup_enabled && (
                      <>
                        <Flex
                          classes={['form-image-flex-direction', 'mt-12', 'mb-20']}
                          variant='space-between'
                        >
                          <Flex variant='column-center'>
                            <UploadedImage
                              published
                              fileUrl={signedImageUrl.marketing_signup_image}
                              id={'marketing-signup'}
                            />
                            <InputFile
                              name='marketingSignupImage'
                              dataTestId='opportunity-marketing-signup-image'
                              handleFiles={async (file: any) => {
                                if (handleFileUpload && file) {
                                  const data = await handleFileUpload(file as File, 'image')
                                  if (data && data.data) {
                                    setFieldValue('marketing_signup_image', data.data.key)
                                    const { url } = await generatePreview(data.data.key)
                                    setSignedImageUrl({
                                      ...signedImageUrl,
                                      marketing_signup_image: url,
                                    })
                                  } else if (data?.message) {
                                    handleErrorMessage && handleErrorMessage(data?.message)
                                  }
                                } else {
                                  setFieldValue('marketing_signup_image', '')
                                  setSignedImageUrl({
                                    ...signedImageUrl,
                                    marketing_signup_image: '',
                                  })
                                }
                              }}
                              {...(Object.keys(uploadFilesMetaData.marketing_signup_image).length >
                              0
                                ? { file: uploadFilesMetaData.marketing_signup_image }
                                : {})}
                              label='Marketing landing image (desktop)'
                              dirty={dirty}
                              errors={{
                                marketingSignupImage:
                                  !!errors && errors.marketing_signup_image
                                    ? errors.marketing_signup_image
                                    : null,
                              }}
                              touched={{
                                marketingSignupImage: !!touched && !!touched.marketing_signup_image,
                              }}
                              handleDeleteFile={() => {
                                setFieldValue('marketing_signup_image', '')
                                setSignedImageUrl({
                                  ...signedImageUrl,
                                  marketing_signup_image: '',
                                })
                              }}
                            />
                            <p
                              className='input-label'
                              style={{ marginTop: '8px', fontWeight: 400 }}
                            >
                              Recommended: 1440 × 1620 px (8:9). Fills the fixed left signup panel
                              (50% width × full height) on standard screens. Keep branding in center
                              80%.
                            </p>
                          </Flex>
                          <Flex variant='column-center'>
                            <UploadedImage
                              published
                              fileUrl={signedImageUrl.marketing_signup_mobile_image}
                              id={'marketing-signup-mobile'}
                            />
                            <InputFile
                              name='marketingSignupMobileImage'
                              dataTestId='opportunity-marketing-signup-mobile-image'
                              handleFiles={async (file: any) => {
                                if (handleFileUpload && file) {
                                  const data = await handleFileUpload(file as File, 'image')
                                  if (data && data.data) {
                                    setFieldValue('marketing_signup_mobile_image', data.data.key)
                                    const { url } = await generatePreview(data.data.key)
                                    setSignedImageUrl({
                                      ...signedImageUrl,
                                      marketing_signup_mobile_image: url,
                                    })
                                  } else if (data?.message) {
                                    handleErrorMessage && handleErrorMessage(data?.message)
                                  }
                                } else {
                                  setFieldValue('marketing_signup_mobile_image', '')
                                  setSignedImageUrl({
                                    ...signedImageUrl,
                                    marketing_signup_mobile_image: '',
                                  })
                                }
                              }}
                              {...(Object.keys(uploadFilesMetaData.marketing_signup_mobile_image)
                                .length > 0
                                ? { file: uploadFilesMetaData.marketing_signup_mobile_image }
                                : {})}
                              label='Marketing landing image (mobile, optional)'
                              dirty={dirty}
                              errors={{
                                marketingSignupMobileImage:
                                  !!errors && errors.marketing_signup_mobile_image
                                    ? errors.marketing_signup_mobile_image
                                    : null,
                              }}
                              touched={{
                                marketingSignupMobileImage:
                                  !!touched && !!touched.marketing_signup_mobile_image,
                              }}
                              handleDeleteFile={() => {
                                setFieldValue('marketing_signup_mobile_image', '')
                                setSignedImageUrl({
                                  ...signedImageUrl,
                                  marketing_signup_mobile_image: '',
                                })
                              }}
                            />
                            <p
                              className='input-label'
                              style={{ marginTop: '8px', fontWeight: 400 }}
                            >
                              Recommended: 1200 × 500 px (12:5). Fills full width on mobile. If
                              omitted, desktop image is used.
                            </p>
                          </Flex>
                        </Flex>
                        {values.opportunity_slug && (
                          <Flex classes={['mb-20']} variant='column'>
                            <div className='input-label'>Campaign signup link (path)</div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <Input
                                name='marketingSignupLink'
                                type='text'
                                value={buildMarketingSignupPathLink(values.opportunity_slug)}
                                disabled
                                dataTestId='opportunity-marketing-signup-link'
                              />
                              <Button
                                variant='secondary-btn'
                                text='Copy'
                                dataTestId='opportunity-copy-marketing-signup-link-button'
                                handleClick={() => {
                                  void copyLinkToClipboard(
                                    buildMarketingSignupPathLink(values.opportunity_slug),
                                  )
                                }}
                              />
                            </div>
                            <div className='input-label' style={{ marginTop: '16px' }}>
                              UTM parameters (optional)
                            </div>
                            <div className='form-grid form-grid--cols-3 mt-12'>
                              {CAMPAIGN_UTM_PARAM_KEYS.map((utmKey) => (
                                <div className='field-container' key={utmKey}>
                                  <Input
                                    name={utmKey}
                                    labelText={CAMPAIGN_UTM_PARAM_LABELS[utmKey]}
                                    type='text'
                                    value={campaignUtmParams[utmKey]}
                                    placeholder={`e.g. ${
                                      utmKey === 'utm_source'
                                        ? 'linkedin'
                                        : utmKey === 'utm_medium'
                                        ? 'social'
                                        : utmKey === 'utm_campaign'
                                        ? 'summer_2026'
                                        : ''
                                    }`}
                                    dataTestId={`opportunity-marketing-utm-${utmKey}`}
                                    handleChange={(event) => {
                                      setCampaignUtmParams((previous) => ({
                                        ...previous,
                                        [utmKey]: event.target.value,
                                      }))
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            <div className='input-label' style={{ marginTop: '12px' }}>
                              Campaign signup link (query + UTMs)
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <Input
                                name='marketingSignupQueryLink'
                                type='text'
                                value={buildMarketingSignupQueryLink(
                                  values.opportunity_slug,
                                  campaignUtmParams,
                                )}
                                disabled
                                dataTestId='opportunity-marketing-signup-query-link'
                              />
                              <Button
                                variant='secondary-btn'
                                text='Copy'
                                dataTestId='opportunity-copy-marketing-signup-query-link-button'
                                handleClick={() => {
                                  void copyLinkToClipboard(
                                    buildMarketingSignupQueryLink(
                                      values.opportunity_slug,
                                      campaignUtmParams,
                                    ),
                                  )
                                }}
                              />
                            </div>
                            <div
                              className='publish-warning-sub-heading'
                              style={{ marginTop: '8px' }}
                            >
                              <p>
                                Share either link in marketing campaigns. Visitors will see this
                                image on the signup page and be redirected to this opportunity after
                                signup. Fill in UTM fields above to generate a tracked query link.
                              </p>
                            </div>
                          </Flex>
                        )}
                      </>
                    )}
                  </div>
                </section>

                <section className='form-section'>
                  <H6
                    classes={['mt-28', 'mb-32', 'form-section-title']}
                    text='Opportunity Documents'
                  />
                  <Line />
                  <Flex classes={['form-image-flex-direction', 'mt-40']} variant='space-between'>
                    <FieldArray
                      name='documents'
                      render={(arrayHelpers) => (
                        <>
                          {values?.documents?.map((doc: object, idx: number) => {
                            const documentTitleFieldName = `document-${idx}-title`
                            const documentUrlError =
                              errors?.documents?.[idx] &&
                              typeof errors.documents[idx] === 'object' &&
                              'url' in (errors.documents[idx] as object)
                                ? (errors.documents[idx] as { url?: string }).url ?? null
                                : null

                            return (
                              <Flex variant='column' classes={['mb-24']} key={idx}>
                                <div className='input-label'>Document {idx + 1}</div>
                                <Input
                                  name={documentTitleFieldName}
                                  placeholder={`Document ${idx + 1} Title`}
                                  dataTestId={`opportunity-document-${idx + 1}-title`}
                                  type='text'
                                  required
                                  value={values.documents?.[idx]?.title}
                                  handleChange={(e) => {
                                    setFieldValue(`documents[${idx}].title`, e.target.value)

                                    if (e.target.value !== '') {
                                      setFieldTouched(`documents[${idx}].title`, true)
                                    } else {
                                      setFieldTouched(`documents[${idx}].title`, false)
                                    }
                                  }}
                                  dirty={dirty}
                                  errors={{
                                    [documentTitleFieldName]: documentUrlError,
                                  }}
                                  touched={{
                                    [documentTitleFieldName]: Boolean(
                                      getIn(touched, `documents.${idx}.url`) ||
                                        getIn(touched, `documents.${idx}.title`),
                                    ),
                                  }}
                                />
                                <InputFile
                                  name={`opportunityDoc${idx + 1}`}
                                  handleFiles={async (file: any) => {
                                    if (handleFileUpload && file) {
                                      const data = await handleFileUpload(file as File, 'doc')
                                      if (data && data.data) {
                                        setFieldValue(`documents[${idx}]`, {
                                          title: values.documents?.[idx]?.title ?? '',
                                          url: data.data.key,
                                        })
                                      } else if (data?.message) {
                                        handleErrorMessage && handleErrorMessage(data.message)
                                      }
                                    }
                                  }}
                                  file={uploadFilesMetaData.documents[idx]}
                                  label={''}
                                  dirty={dirty}
                                  errors={{
                                    documents:
                                      !!errors && errors.documents?.[idx]?.toString()
                                        ? errors.documents?.[idx].toString()
                                        : null,
                                  }}
                                  handleDeleteFile={() => {
                                    arrayHelpers.replace(idx, {
                                      title: '',
                                      url: '',
                                    })
                                  }}
                                  type='doc'
                                  dataTestId={`opportunity-document-${idx + 1}`}
                                />
                              </Flex>
                            )
                          })}
                          {/* Images Button section*/}
                          <Flex variant='align-center' classes={['mt-16']}>
                            <Button
                              classes={['auto-btn-width']}
                              variant={'primary-btn'}
                              text='Add 1 more document'
                              dataTestId='opportunity-document-add-button'
                              handleClick={() => {
                                arrayHelpers.push({
                                  title: '',
                                  url: '',
                                })
                              }}
                            />
                            {values?.documents?.length > 1 && (
                              <Button
                                classes={['auto-btn-width', 'ml-8']}
                                variant={'primary-btn'}
                                text='Remove last document'
                                dataTestId='opportunity-document-delete-last-button'
                                handleClick={() => {
                                  arrayHelpers.pop()
                                }}
                              />
                            )}
                          </Flex>
                        </>
                      )}
                    />
                  </Flex>
                </section>

                <section className='form-section form-section--actions'>
                  <Line />
                  <Flex variant='flex-end'>
                    <div className='project-form-save-button-wrapper'>
                      <Flex>
                        <Button
                          classes={['auto-btn-width']}
                          variant={'primary-btn'}
                          dataTestId='opportunity-save-button'
                          text='Save'
                          handleClick={handleSaveClick}
                        />
                      </Flex>
                      <div
                        className='project-form-error-message-wrapper'
                        data-testid='opportunity-error-message'
                      />
                    </div>
                  </Flex>
                </section>
              </form>
            )
          }}
        </Formik>
      </div>
    </>
  )
}

export { OpportunityFormPresenter }
